// src/render/container.ts

import type { SectionAttrs } from "../types";
import { getAlignClass } from "../utils/positions";
import { attachAutoTextScale } from "../utils/autoText";
import { Z } from "../utils/constants";

// SSR-safe debug flag
const __SC_DEBUG =
  typeof window !== "undefined" &&
  typeof window.location !== "undefined" &&
  new URLSearchParams(window.location.search).has("debug");

// Tailwind-safe text color mapping; avoids dynamic class names getting purged.
function resolveTextColor(text?: string | null): { klass: string; inline?: string } {
  const t = (text || "").toString().trim().toLowerCase();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(t)) return { klass: "", inline: t };
  if (/^rgba?\(.+\)$/.test(t)) return { klass: "", inline: t };
  if (/^hsla?\(.+\)$/.test(t)) return { klass: "", inline: t };
  if (/^var\(--[a-z0-9\-]+\)$/i.test(t)) return { klass: "", inline: t };
  const MAP: Record<string, string> = { white: "text-white", light: "text-white", black: "text-black", dark: "text-black" };
  const PASS = new Set(["text-white","text-black","text-gray-900","text-gray-100","text-neutral-900","text-neutral-100"]);
  if (PASS.has(t)) return { klass: t };
  if (MAP[t]) return { klass: MAP[t] };
  return { klass: "text-white" }; // why: legibility default on media-heavy slides
}

// Helper for numeric option parsing (used by auto text scaling)
function numOrUndef(v: unknown): number | undefined {
  if (v === "" || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

// Microtask helper for older engines.
function scheduleMicrotask(fn: () => void) {
  if (typeof queueMicrotask === "function") queueMicrotask(fn);
  else Promise.resolve().then(fn);
}

// Compat removal observer: watch nearest parent (cheap) AND body subtree (legacy).
function observeRemoval(target: HTMLElement, onRemove: () => void): { disconnect(): void } {
  let done = false;
  const disconnectors: Array<() => void> = [];

  const maybeFire = () => {
    if (done) return;
    if (!document.body.contains(target)) {
      done = true;
      try { onRemove(); } finally {
        disconnectors.forEach(fn => { try { fn(); } catch {} });
      }
    }
  };

  // Parent-scoped
  try {
    const parent: Node | null = target.parentNode || document.body;
    if (parent) {
      const mo = new MutationObserver(maybeFire);
      mo.observe(parent, { childList: true, subtree: parent === document.body });
      disconnectors.push(() => mo.disconnect());
    }
  } catch {}

  // Body subtree (covers ancestor removals/reparents)
  try {
    const moBody = new MutationObserver(maybeFire);
    moBody.observe(document.body, { childList: true, subtree: true });
    disconnectors.push(() => moBody.disconnect());
  } catch {}

  scheduleMicrotask(maybeFire);
  requestAnimationFrame(maybeFire);

  return {
    disconnect() {
      if (done) return;
      done = true;
      disconnectors.forEach(fn => { try { fn(); } catch {} });
    }
  };
}

/**
 * Creates the main wrapper for section content.
 * NOTE: Padding, max-height, and orientation-sensitive layout are handled in sectionContent.ts.
 * This file intentionally avoids setting any padding or sizing styles to prevent conflicts.
 */
export function createSectionContainer(attrs: SectionAttrs): HTMLElement {
  // Outer container
  const container = document.createElement("div");
  container.dataset.role = "section-container";

  const { klass: textClass, inline: textInline } = resolveTextColor(attrs.text);
  container.className = `relative container ${textClass} ${getAlignClass(attrs.align || "left")}`;
  container.style.zIndex = String(Z.CONTENT);
  Object.assign(container.style, { height: "100%", overflow: "hidden" });
  if (textInline) (container.style as any).color = textInline;

  // Inner content node
  const content = document.createElement("div");
  content.dataset.role = "section-content";
  content.className = "content";
  content.style.boxSizing = "border-box";
  (content.style as any).minWidth = "0";
  content.style.overflow = "hidden";

  // Dev outlines
  if (__SC_DEBUG) {
    container.style.outline = "1px dashed rgba(0,255,0,0.5)";
    container.style.backgroundColor = "rgba(0,255,0,0.03)";
    content.style.outline = "1px dashed rgba(0,0,255,0.5)";
  }

  container.appendChild(content);

  // Auto text scaling (dispose when node is removed)
  const autoRaw = (attrs as any)["autoscale-text"];
  const auto = (typeof autoRaw === "string") ? autoRaw.toLowerCase() : autoRaw;
  if (auto && auto !== "off" && auto !== false && auto !== "false" && auto !== "0") {
    try { (container as any).__autoTextDispose?.(); } catch {}
    try { (container as any).__autoTextMO?.disconnect?.(); } catch {}

    const opts: any = {};
    const vMin = numOrUndef((attrs as any)["text-min"]);
    const vMax = numOrUndef((attrs as any)["text-max"]);
    const vLines = numOrUndef((attrs as any)["text-lines"]);
    const vLineH = numOrUndef((attrs as any)["line-height"]);
    if (vMin != null) opts.minPx = vMin;
    if (vMax != null) opts.maxPx = vMax;
    if (vLines != null) opts.targetLines = vLines;
    if (vLineH != null) opts.lineHeight = vLineH;

    (container as any).__autoTextDispose = attachAutoTextScale(content, opts);

    requestAnimationFrame(() => {
      if (!container.isConnected) return;
      const handle = observeRemoval(container, () => {
        try { (container as any).__autoTextDispose?.(); } catch {}
        try { (container as any).__autoTextMO?.disconnect?.(); } catch {}
        (container as any).__autoTextDispose = undefined;
        (container as any).__autoTextMO = undefined;
      });
      (container as any).__autoTextMO = handle;
    });
  }

  return container;
}